/**
 * The Inbox's counts (1.N-1 deliverable 2): `inbox.counts` kept in the query cache from the
 * event stream and from refetches after the events that change what the Inbox holds.
 */
import { type InboxCountsV1, inboxCounts } from '@codex/contracts';
import { type QueryClient, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';
import { api } from '../auth/client.js';
import { appEvents } from '../editor-services/events.js';
import { debounced, INBOX_REFRESH_EVENTS, totalOpen } from './inbox-model.js';

export const countsKey = ['inbox', 'counts'] as const;
export const listKey = ['inbox', 'list'] as const;
export const REFRESH_MS = 500;

/** One set of subscriptions per query client, shared by every mounted user of the counts. */
const shared = new WeakMap<QueryClient, { users: number; stop: () => void }>();

export function subscribeCounts(client: QueryClient): () => void {
  let entry = shared.get(client);
  if (!entry) {
    const refresh = debounced(
      () => void client.invalidateQueries({ queryKey: countsKey }),
      REFRESH_MS,
    );
    const stops = [
      appEvents.subscribe('inbox.counts', (event) => {
        client.setQueryData<InboxCountsV1>(countsKey, event.data);
        notifyCounts(client, totalOpen(event.data));
      }),
      ...INBOX_REFRESH_EVENTS.map((type) => appEvents.subscribe(type, refresh)),
    ];
    entry = {
      users: 0,
      stop: () => {
        refresh.cancel();
        for (const stop of stops) stop();
      },
    };
    shared.set(client, entry);
  }
  entry.users += 1;
  const held = entry;
  return () => {
    held.users -= 1;
    if (held.users === 0) {
      held.stop();
      if (shared.get(client) === held) shared.delete(client);
    }
  };
}

export function useInboxCounts(enabled = true) {
  const client = useQueryClient();
  const query = useQuery({
    queryKey: countsKey,
    queryFn: () => api(inboxCounts),
    enabled,
    retry: false,
  });
  useEffect(() => (enabled ? subscribeCounts(client) : undefined), [client, enabled]);
  return query;
}

// ---- the list follows the total (deliverable 11)

let inFlight = 0;
let deferred = false;

/** Marks a dismissal in flight; the returned function ends it and runs a deferred list refresh. */
export function beginDismissal(client: QueryClient): () => void {
  inFlight += 1;
  return () => {
    inFlight -= 1;
    if (inFlight === 0 && deferred) {
      deferred = false;
      void client.invalidateQueries({ queryKey: listKey });
    }
  };
}

const lastTotal = new WeakMap<QueryClient, number>();

/** A new total that differs from the last one seen invalidates the list's first page. */
export function notifyCounts(client: QueryClient, total: number): void {
  const before = lastTotal.get(client);
  lastTotal.set(client, total);
  if (before === undefined || before === total) return;
  if (inFlight > 0) {
    deferred = true;
    return;
  }
  void client.invalidateQueries({ queryKey: listKey });
}

/** Watches the cached counts (event or refetch) and refreshes the list when the total moves. */
export function useListFollowsCounts(): void {
  const client = useQueryClient();
  const { data } = useInboxCounts();
  const total = data === undefined ? undefined : totalOpen(data);
  useEffect(() => {
    if (total !== undefined) notifyCounts(client, total);
  }, [client, total]);
}
